/**
 * Brings the client profile's right-hand column to phones and tablets.
 *
 * That column is laid out beside the form only from `xl` (1280px) up, so below that — every phone, and an iPad in portrait —
 * the Financial Snapshot and every explanatory card simply did not exist: a client could enter
 * $3.8M of assets and never be shown a total, and ticking a goal produced nothing on screen.
 * This renders the SAME content (passed as children, never a second copy of it) as a bar
 * pinned to the bottom, which opens into a sheet.
 *
 * TWO DETAILS THAT MATTER.
 *
 * The children are mounted whether or not the sheet is open — hidden with CSS, never unmounted —
 * because the Financial Snapshot computes as it renders and reports its figure back up for the
 * collapsed bar to display. Unmounting it would leave the bar with nothing to show until the
 * first tap, and re-running the calculation on every open.
 *
 * The bar stops short of the right edge (`right-[88px]`) instead of spanning the full width,
 * leaving the floating AWise button its corner. The bottom of a phone screen is contested — the
 * chat button, the card's own Back/Next row and this bar all want it — and sitting BESIDE the
 * button keeps everything in one bottom row rather than stacking three things up the screen.
 * See MainAppLayout's `pb-28`, which reserves that row.
 */
import React, { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

interface MobileInsightSheetProps {
    /** What the bar calls the content — "Financial Snapshot", "About your goals". */
    title: string;
    /** Optional headline figure for the collapsed bar, already formatted. Comes from the child
     *  card itself, so the two can never print different numbers. */
    value?: string | null;
    children: React.ReactNode;
}

const MobileInsightSheet = ({ title, value, children }: MobileInsightSheetProps) => {
    const [open, setOpen] = useState(false);

    return (
        <div className="xl:hidden">
            <div className="fixed bottom-4 left-4 right-[88px] z-20">
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    aria-expanded={open}
                    className="w-full min-h-[56px] flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 shadow-cardlg text-left"
                >
                    {/* Two modes, because the small-caps style is a CAPTION: it exists to introduce
                        the figure below it. On the steps that have no figure it was the only thing
                        in the bar, which read as an empty box rather than something worth opening,
                        so there the title is set as a plain readable label instead. */}
                    <span className="min-w-0 flex-grow">
                        {value ? (
                            <>
                                <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 truncate">{title}</span>
                                <span className="block text-xl font-extrabold bg-gradient-to-r from-blue-400 to-teal-400 text-transparent bg-clip-text">
                                    {value}
                                </span>
                            </>
                        ) : (
                            <span className="block text-[15px] font-semibold text-gray-900 truncate">{title}</span>
                        )}
                    </span>
                    {/* The chevron sits in a chip so it reads as a control. On its own it was easy
                        to miss at this size, and nothing else about the bar said it opened. */}
                    <span className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                        <ChevronUp size={18} className="text-brand-navy" aria-hidden="true" />
                    </span>
                </button>
            </div>

            {/* Always mounted — see the note above. Inert and invisible while closed. */}
            <div
                className={`fixed inset-0 z-[60] flex items-end transition-opacity duration-200 ${
                    open ? 'opacity-100' : 'opacity-0 invisible pointer-events-none'
                }`}
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <button
                    type="button"
                    className="absolute inset-0 bg-slate-900/40"
                    aria-label={`Close ${title}`}
                    onClick={() => setOpen(false)}
                />
                <div className="relative w-full max-h-[85vh] overflow-y-auto bg-white rounded-t-2xl shadow-2xl px-4 pt-2 pb-8">
                    <div className="flex justify-center pb-1">
                        <span className="w-10 h-1 rounded-full bg-gray-300" aria-hidden="true" />
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="w-11 h-11 -mr-1 flex items-center justify-center rounded-full text-brand-navy hover:bg-slate-100"
                            aria-label={`Close ${title}`}
                        >
                            <ChevronDown size={22} aria-hidden="true" />
                        </button>
                    </div>
                    {children}
                </div>
            </div>
        </div>
    );
};

export default MobileInsightSheet;
